<template>
  <div
    v-if="state"
    ref="rootRef"
    class="san-virtual-hits"
  >
    <slot v-bind="state">
      <slot
        v-if="nbHits > 0"
        name="list"
        v-bind="items"
      >
        <component
          :is="listTag"
          class="san-virtual-hits--list"
          :class="[`view--${view}`]"
        >
          <component
            :is="itemTag"
            ref="probeRef"
            class="san-virtual-hits--list--probe"
          >
            <slot name="probe" />
            <q-resize-observer @resize="onResize" />
          </component>

          <component
            :is="itemTag"
            v-for="item in items"
            :key="item.index"
            v-intersection="onIntersection"
            class="san-virtual-hits--list--item"
            :data-index="item.index"
            :data-page="item.page"
            :style="{
              width: `${itemSize.width}px`,
              height: `${itemSize.height}px`,
            }"
            @click="state.sendEvent('click:internal', item, 'Hit Clicked')"
            @auxclick="state.sendEvent('click:internal', item, 'Hit Clicked')"
          >
            <!-- {{ console.log({ virtualRows, totalSizeRows, totalSizeColumns }) }} -->
            <!-- {{ console.log({ columnCount, row: row.index, column: item.index, key: item.key, item: row.index * columnCount + item.index }) }} -->
            <slot
              v-if="!item.isLoaded"
              name="placeholder"
              v-bind="item"
            >
              placeholder: { index: {{ item.index }}, page: {{ item.page }} }
            </slot>
            <slot
              v-else
              name="item"
              v-bind="item"
            >
              item: { index: {{ item.index }}, page: {{ item.page }} }
            </slot>
          </component>
        </component>
      </slot>

      <div
        v-else
        class="no-results"
      >
        <slot name="no-results">
          No results
        </slot>
      </div>
    </slot>
  </div>
</template>

<script setup>
import { ref, toRaw, watch, computed, nextTick, onMounted } from 'vue' // eslint-disable-line
import { connectInfiniteHits } from 'instantsearch.js/es/connectors'
import { useConnector } from '@/composables'
import { isEqual } from 'lodash'
import { createTaggedQueue } from '@/helpers'

defineOptions({ name: 'SanVirtualHits' })
const props = defineProps({
  showBanner: { type: Boolean, default: true },
  showButtonPrev: { type: Boolean, default: false },
  showButtonNext: { type: Boolean, default: false },
  escapeHTML: { type: Boolean, default: true },
  transformItems: { type: Function, default: undefined },
  cache: { type: Object, default: null },
  listTag: { type: String, default: 'ol' },
  itemTag: { type: String, default: 'li' },
  // itemHeight: { type: Number, required: true, validator: val => Number.isInteger(val) && val >= 1 },
  view: { type: String, default: 'list', validator: val => val === 'list' || val === 'grid' } // list || grid || table
})

const queue = createTaggedQueue(300)

const cachedHits = ref()
const cachedState = ref()
const items = ref([])
const rootRef = ref()
const probeRef = ref()
const itemSize = ref({ width: 0, height: 0 })

const getStateWithoutPage = ({ page, ...rest } = {}) => rest
const getInMemoryCache = () => {
  return {
    read: ({ state }) => {
      const hits = isEqual(toRaw(cachedState.value), getStateWithoutPage(state)) ? cachedHits.value : null
      // console.log('%cgetInMemoryCache:read', 'color:orange', hits)
      return hits
    },
    write: ({ state, hits }) => {
      cachedState.value = getStateWithoutPage(state)
      cachedHits.value = hits
      // console.log('%cgetInMemoryCache:write', 'color:cyan', state)
    }
  }
}

const state = useConnector(renderFn => connectInfiniteHits((renderOptions, isFirstRender) => {
  const { instantSearchInstance } = renderOptions // eslint-disable-line
  const { helper, mainHelper: { derivedHelpers } } = instantSearchInstance // eslint-disable-line

  // instantSearchInstance.on('render', e => {
  //   console.log('%chelper', 'color:lime', helper)
  //   // // Get the search helper (docs are here https://community.algolia.com/algoliasearch-helper-js/reference.html)

  //   // // Get the query used for the last search
  //   // console.log('%cquery used', 'color:lime', helper.lastResults?.query)

  //   // // Get the number of results for the last search
  //   // console.log('%cnumber of results', 'color:orange', helper.lastResults?.nbHits)

  //   // // Get the refinements used in the last search results
  //   // console.log('%crefinements used', 'color:crimson', helper.lastResults?.getRefinements())

  //   // // Get the current refinement on brand
  //   // console.log('%crefinements currently set for Brand', 'color:accent', helper.getRefinements('brands'))
  // })

  renderFn({
    ...renderOptions,
    refinePage: async page => {
      // if ((page - 1) in cachedHits.value) return
      helper.setPage(page)
      helper.search()
    }
  }, isFirstRender)
}, params => {
  console.log('%cunmount', 'color:orange')
}), {
  $$widgetType: 'ais.infiniteHits',
  showBanner: props.showBanner,
  showPrevious: props.showButtonPrev,
  escapeHTML: props.escapeHTML,
  transformItems: props.transformItems,
  cache: props.cache || getInMemoryCache()
})

const nbHits = computed(() => state.value?.results.nbHits || 0)
const hitsPerPage = computed(() => state.value?.results.hitsPerPage || 0)

const onResize = ({ width, height }) => {
  itemSize.value = { width, height }
  console.log('%conResize', 'color:orange', { width, height })
}

const onIntersection = async entry => {
  const page = parseInt(entry.target.dataset.page, 10)
  const nextPageIndex = page

  if (cachedHits.value && entry.isIntersecting && !(nextPageIndex in cachedHits.value)) {
    queue.enqueue(() => state.value.refinePage(nextPageIndex), nextPageIndex)
  }
}
// const items = computed(() => {
//   if (nbHits.value < 1) return []

//   const items = []
//   for (let index = 0; index < nbHits.value; index++) {
//     const page = Math.floor(index / hitsPerPage.value) + 1
//     const loadedItemIndex = index % hitsPerPage.value
//     // const loadedItemIndex = (index + 1) % hitsPerPage.value
//     const loadedPageItems = cachedHits.value ? cachedHits.value[page - 1] : []
//     let item = { index, page, isLoaded: cachedHits.value ? page - 1 in cachedHits.value : false }
//     // if (index === 0 || loadedItemIndex === 0) item.isObserved = true
//     if (loadedPageItems) item = { ...item, ...loadedPageItems[loadedItemIndex] }
//     items[index] = item
//   }
//   console.log('%citems', 'color:orange', cachedHits.value)

//   return items
// })

watch(() => cachedHits.value, val => {
  items.value = []

  if (nbHits.value < 1) return

  for (let index = 0; index < nbHits.value; index++) {
    const pageIndex = Math.floor(index / hitsPerPage.value)
    const loadedItemIndex = index % hitsPerPage.value
    const item = { index, page: pageIndex + 1, isLoaded: false }

    if (val[pageIndex] && val[pageIndex][loadedItemIndex]) {
      // Merge the cached hit properties into our item
      Object.assign(item, val[pageIndex][loadedItemIndex])
      item.isLoaded = true
    }

    items.value[index] = item

    // const loadedPageItems = val ? val[page - 1] : []
    // let item = { index, page, isLoaded: val ? page - 1 in val : false }
    // console.log('%cloadedPageItems', 'color:orange', { val, page: page - 1, loadedItemIndex, loadedPageItems })
    // if (loadedPageItems.length > 0) item = { ...item, ...loadedPageItems[loadedItemIndex] }
    // items.value[index] = item
  }
}, { deep: true })
// watch(() => cachedHits.value, val => {
//   if (nbHits.value < 1) {
//     items.value = []
//     return
//   }

//   for (let index = 0; index < nbHits.value; index++) {
//     const page = Math.floor(index / hitsPerPage.value) + 1
//     const loadedItemIndex = (index + 1) % hitsPerPage.value
//     const loadedPageItems = val[page - 1]
//     let item = { index, page, isLoaded: page - 1 in val }
//     if (index === 0 || loadedItemIndex === 0) item.isObserved = true
//     if (loadedPageItems) item = { ...item, ...loadedPageItems[loadedItemIndex] }
//     items.value[index] = item
//   }
// }, { deep: true })

watch(() => state.value?.instantSearchInstance.helper.state.index, (newIndex, oldIndex) => {
  if (newIndex === oldIndex) return
  queue.reset()
})
watch(() => state.value?.instantSearchInstance.helper.state, (newState, oldState) => {
  if (isEqual(newState, oldState)) return
  // console.log('%cinstantSearchInstance.helper.state', 'color:lime', { newState, oldState, queue })
  // queue.reset()
})
</script>
